import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COOLDOWN_KEY = "aife_popup_dismissed_until";
const SHOWN_KEY = "aife_popup_shown_session";
const COOLDOWN_HOURS = 24;

const AUDIENCES = [
  { id: "school", label: "School" },
  { id: "college", label: "College" },
  { id: "corporate", label: "Corporate" },
  { id: "individual", label: "Individual" },
];

function isCoolingDown() {
  try {
    const until = parseInt(localStorage.getItem(COOLDOWN_KEY) || "0", 10);
    return until && Date.now() < until;
  } catch (_) {
    return false;
  }
}

function startCooldown() {
  try {
    localStorage.setItem(
      COOLDOWN_KEY,
      String(Date.now() + COOLDOWN_HOURS * 60 * 60 * 1000)
    );
    sessionStorage.setItem(SHOWN_KEY, "1");
  } catch (_) {}
}

function alreadyShownThisSession() {
  try {
    return sessionStorage.getItem(SHOWN_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function Modal({ children, onClose, testId }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6"
      data-testid={testId}
    >
      <button
        type="button"
        aria-label="Close popup"
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: "rgba(10,14,31,0.5)", backdropFilter: "blur(4px)" }}
      />
      <div
        className="relative w-full md:max-w-[460px] bg-white border hairline-strong shadow-2xl"
        style={{ borderRadius: 4 }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center rounded-sm hover:bg-[var(--canvas-deep)] transition-colors"
          data-testid="popup-close"
        >
          <X size={16} strokeWidth={1.75} />
        </button>
        {children}
      </div>
    </div>
  );
}

function PopupForm({
  source,
  title,
  body,
  showName = true,
  showQueryField = false,
  queryLabel = "What are you looking for?",
  queryDefault = "",
  ctaLabel,
  onDone,
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [audience, setAudience] = useState("");
  const [query, setQuery] = useState(queryDefault);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/popup-capture`, {
        email: email.trim(),
        name: name.trim() || null,
        audience: audience || null,
        query: query.trim() || null,
        source,
      });
      toast.success("Thank you. We'll be in touch.");
      startCooldown();
      onDone();
    } catch (_) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-6 md:p-8" data-testid={`popup-form-${source}`}>
      <p className="mono-label-gold mb-3">{title}</p>
      <p
        className="font-display text-[22px] md:text-[24px] tracking-tight leading-tight"
        style={{ fontWeight: 500 }}
      >
        {body.headline}
      </p>
      <p className="mt-3 text-[14px]" style={{ color: "var(--ink-secondary)" }}>
        {body.sub}
      </p>

      <div className="mt-5 space-y-4">
        {showName && (
          <div>
            <label className="form-label" htmlFor={`pn-${source}`}>Name</label>
            <input
              id={`pn-${source}`}
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="popup-name"
            />
          </div>
        )}
        {showQueryField && (
          <div>
            <label className="form-label" htmlFor={`pq-${source}`}>{queryLabel}</label>
            <input
              id={`pq-${source}`}
              type="text"
              className="form-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="popup-query"
            />
          </div>
        )}
        <div>
          <label className="form-label" htmlFor={`pe-${source}`}>Email *</label>
          <input
            id={`pe-${source}`}
            type="email"
            required
            className="form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="popup-email"
          />
        </div>
        <div>
          <p className="form-label">I am a</p>
          <div className="flex flex-wrap gap-2">
            {AUDIENCES.map((a) => (
              <button
                type="button"
                key={a.id}
                onClick={() => setAudience(a.id)}
                className={`filter-pill ${audience === a.id ? "is-active" : ""}`}
                aria-pressed={audience === a.id}
                data-testid={`popup-audience-${a.id}`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="popup-submit">
          {loading ? "Sending…" : ctaLabel}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="text-[13px] link-underline"
          style={{ color: "var(--ink-tertiary)" }}
          data-testid="popup-dismiss"
        >
          {source === "popup_exit" ? "Not interested" : "No thanks, I'll keep browsing"}
        </button>
      </div>
    </form>
  );
}

export default function Popups() {
  const [active, setActive] = useState(null); // 'scroll' | 'search' | 'exit' | null
  const firedRef = useRef({ scroll: false, search: false, exit: false });

  // Skip everything if we've shown one this session or cooldown is active
  const canShow = () => !alreadyShownThisSession() && !isCoolingDown();

  // Scroll-depth trigger — fire only in the mid-engagement window so we never
  // intercept users who have already reached the enquiry form (deep scroll).
  useEffect(() => {
    const onScroll = () => {
      if (firedRef.current.scroll || !canShow()) return;
      const sb = document.documentElement;
      const scrolled = (window.scrollY + window.innerHeight) / sb.scrollHeight;
      // Hard guard: if the enquiry section is anywhere in/above the viewport,
      // never fire — the user is already at the conversion point.
      const enquireEl = document.getElementById("enquire");
      if (enquireEl) {
        const top = enquireEl.getBoundingClientRect().top;
        if (top < window.innerHeight * 0.9) return;
      }
      if (scrolled >= 0.45 && scrolled <= 0.6) {
        firedRef.current.scroll = true;
        setActive("scroll");
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Exit-intent (desktop only — mouse leaves toward top)
  useEffect(() => {
    const onLeave = (e) => {
      if (firedRef.current.exit || !canShow()) return;
      if (e.clientY <= 0) {
        firedRef.current.exit = true;
        setActive("exit");
      }
    };
    document.addEventListener("mouseout", onLeave);
    return () => document.removeEventListener("mouseout", onLeave);
  }, []);

  // Site-search trigger — listen for a custom event from a future search widget
  useEffect(() => {
    const onSearch = (e) => {
      if (firedRef.current.search || !canShow()) return;
      firedRef.current.search = true;
      setActive("search");
      // Pre-fill query if supplied
      if (e?.detail?.query) {
        // Stored via setActive — actual prefill happens via prop below
        window.__aife_search_query = e.detail.query;
      }
    };
    window.addEventListener("aife:search", onSearch);
    return () => window.removeEventListener("aife:search", onSearch);
  }, []);

  const dismiss = () => {
    startCooldown();
    setActive(null);
  };

  if (!active) return null;

  if (active === "scroll") {
    return (
      <Modal onClose={dismiss} testId="popup-scroll">
        <PopupForm
          source="popup_scroll"
          title="GET THE FULL CATALOG"
          body={{
            headline: "11 launch certifications. 3 tiers. 4 audiences.",
            sub: "Custom-scoped proposals in 48 hours. Tell us where you're coming from — we'll send the right catalog to your inbox.",
          }}
          ctaLabel="Send me the catalog →"
          onDone={dismiss}
        />
      </Modal>
    );
  }

  if (active === "search") {
    const q = (typeof window !== "undefined" && window.__aife_search_query) || "";
    return (
      <Modal onClose={dismiss} testId="popup-search">
        <PopupForm
          source="popup_search"
          title="LOOKING FOR SOMETHING SPECIFIC?"
          body={{
            headline: "Let us match you to the right programme.",
            sub: "Tell us what you're exploring and we'll send a personalized recommendation.",
          }}
          showName={false}
          showQueryField
          queryDefault={q}
          ctaLabel="Get a recommendation →"
          onDone={dismiss}
        />
      </Modal>
    );
  }

  if (active === "exit") {
    return (
      <Modal onClose={dismiss} testId="popup-exit">
        <PopupForm
          source="popup_exit"
          title="BEFORE YOU GO —"
          body={{
            headline: "The AI Innovation Arena is opening Spring 2026 applications.",
            sub: "50+ campuses worldwide. December 2026 Global Finale. Want to bring it to your campus or company?",
          }}
          showName={false}
          ctaLabel="Tell me more →"
          onDone={dismiss}
        />
      </Modal>
    );
  }

  return null;
}
